import { createContext, useContext, useEffect, useState } from "react";

const DEFAULT_RATES = { EUR: 1.0, USD: 1.08, GBP: 0.85, ALL: 101.0 };
const CURRENCY_SYMBOLS = { EUR: "€", USD: "$", GBP: "£", ALL: "L" };
const CURRENCY_NAMES = { EUR: "Euro", USD: "US Dollar", GBP: "British Pound", ALL: "Lek Shqiptar" };

const CurrencyContext = createContext({
    currency: "EUR",
    setCurrency: () => {},
    rates: DEFAULT_RATES,
    setRates: () => {},
    convert: (eur) => eur,
    format: (eur) => `€${eur}`,
});

export function CurrencyProvider({ children }) {
    const [currency, setCurrencyState] = useState(() => {
        if (typeof window === "undefined") return "ALL";
        return localStorage.getItem("dea_currency") || "ALL";
    });
    const [rates, setRates] = useState(DEFAULT_RATES);

    const setCurrency = (c) => {
        const safe = CURRENCY_SYMBOLS[c] ? c : "ALL";
        setCurrencyState(safe);
        if (typeof window !== "undefined") {
            localStorage.setItem("dea_currency", safe);
        }
    };

    const convert = (eurAmount) => {
        const rate = rates?.[currency] || DEFAULT_RATES[currency] || 1;
        return Math.round((Number(eurAmount) || 0) * rate * 100) / 100;
    };

    const format = (eurAmount, opts = {}) => {
        const v = convert(eurAmount);
        const sym = CURRENCY_SYMBOLS[currency] || "€";
        const displayInt = currency === "ALL"; // ALL is integer-only
        const rounded = displayInt ? Math.round(v) : v;
        const formatted = rounded.toLocaleString("en-US", { maximumFractionDigits: displayInt ? 0 : 2 });
        // Lek convention: amount followed by "L" (e.g., "5,555 L")
        if (currency === "ALL") return `${formatted} L`;
        if (opts.symbolAfter) return `${formatted} ${sym}`;
        return `${sym}${formatted}`;
    };

    return (
        <CurrencyContext.Provider value={{ currency, setCurrency, rates, setRates, convert, format }}>
            {children}
        </CurrencyContext.Provider>
    );
}

export function useCurrency() {
    return useContext(CurrencyContext);
}

export const CURRENCIES = ["ALL", "EUR", "USD", "GBP"];
export { CURRENCY_SYMBOLS, CURRENCY_NAMES, DEFAULT_RATES };
